import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  getApiGatewayLogs, getDatabaseLogs,
  getApiGatewayLogsFiltered, getDatabaseLogsFiltered,
} from "@/shared/api/services/infrastructure";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/shared/components/ui/dialog";
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Server, Cpu, MemoryStick, Activity, Wifi, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Clock, Zap, Database,
  Globe, Terminal, TrendingUp, ArrowUp, ArrowDown, FileText,
  Shield, Layers, ChevronRight,
} from "lucide-react";

/* ─────────────── DESIGN TOKENS ─────────────── */
const C = {
  bg: "#04090F",
  card: "#080F1D",
  cardHigh: "#0C1526",
  cardHover: "#101D30",
  border: "rgba(255,255,255,0.06)",
  borderHov: "rgba(34,211,238,0.38)",
  cyan: "#22D3EE",
  cyanSoft: "rgba(34,211,238,0.08)",
  cyanGlow: "rgba(34,211,238,0.18)",
  blue: "#3B82F6",
  blueSoft: "rgba(59,130,246,0.08)",
  purple: "#A78BFA",
  purpleSoft: "rgba(167,139,250,0.08)",
  emerald: "#10B981",
  emeraldSoft: "rgba(16,185,129,0.08)",
  amber: "#F59E0B",
  amberSoft: "rgba(245,158,11,0.08)",
  rose: "#FB7185",
  roseSoft: "rgba(251,113,133,0.08)",
  t1: "#EFF6FF", t2: "#93B4D4", t3: "#4D6E90", t4: "#243A52",
};

/* ─────────────── GLOBAL CSS ─────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap');

.as-root { font-family:'Plus Jakarta Sans',system-ui,sans-serif; }
.as-root *, .as-root *::before, .as-root *::after { box-sizing:border-box; }

/* ── scrollbars ── */
.as-root ::-webkit-scrollbar { width:5px; height:5px; }
.as-root ::-webkit-scrollbar-track { background:rgba(8,15,29,0.5); border-radius:10px; }
.as-root ::-webkit-scrollbar-thumb { background:rgba(34,211,238,0.18); border-radius:10px; }
.as-root ::-webkit-scrollbar-thumb:hover { background:rgba(34,211,238,0.32); }

/* ── select overrides (dark) ── */
.as-root [data-radix-select-trigger],
.as-root [role="combobox"] {
  background:#080F1D !important;
  border:1px solid rgba(255,255,255,0.08) !important;
  color:#EFF6FF !important;
  border-radius:11px !important;
  height:40px !important;
  font-size:13px !important;
  font-weight:600 !important;
  transition:all .2s !important;
  padding:0 14px !important;
  font-family:'Plus Jakarta Sans',system-ui,sans-serif !important;
}
.as-root [data-radix-select-trigger]:hover,
.as-root [role="combobox"]:hover {
  border-color:rgba(34,211,238,0.38) !important;
  color:#22D3EE !important;
  background:#0C1526 !important;
  box-shadow:0 0 12px rgba(34,211,238,0.08) !important;
}
.as-root [data-radix-select-content] {
  background:#080F1D !important;
  border:1px solid rgba(34,211,238,0.18) !important;
  border-radius:14px !important;
  box-shadow:0 16px 48px rgba(0,0,0,0.7) !important;
  font-family:'Plus Jakarta Sans',system-ui,sans-serif !important;
}
.as-root [data-radix-select-item] {
  color:#93B4D4 !important; font-size:13px !important; font-weight:600 !important;
  border-radius:8px !important; transition:all .15s !important;
  font-family:'Plus Jakarta Sans',system-ui,sans-serif !important;
}
.as-root [data-radix-select-item]:hover,
.as-root [data-radix-select-item][data-highlighted] {
  background:rgba(34,211,238,0.09) !important; color:#22D3EE !important;
}

/* ── date inputs — FULLY DARK ── */
.as-date {
  background:#080F1D !important;
  border:1px solid rgba(255,255,255,0.08) !important;
  color:#EFF6FF !important;
  border-radius:11px !important;
  font-size:13px !important; font-weight:600 !important;
  padding:9px 14px !important; width:100% !important;
  transition:all .2s !important;
  font-family:'Plus Jakarta Sans',system-ui,sans-serif !important;
  outline:none !important;
  /* Force dark calendar on webkit */
  color-scheme: dark;
}
.as-date:hover {
  border-color:rgba(34,211,238,0.38) !important;
  background:#0C1526 !important;
}
.as-date:focus {
  border-color:rgba(34,211,238,0.55) !important;
  box-shadow:0 0 0 3px rgba(34,211,238,0.1) !important;
  background:#0C1526 !important;
}
/* Chrome date picker icon color */
.as-date::-webkit-calendar-picker-indicator {
  filter: invert(0.7) sepia(1) saturate(3) hue-rotate(163deg);
  cursor:pointer;
  opacity:0.7;
}
.as-date::-webkit-calendar-picker-indicator:hover { opacity:1; }
/* Firefox */
.as-date::-moz-calendar-picker-indicator { filter:invert(1); }

/* ── dialog override ── */
.as-root [role="dialog"] {
  background:#080F1D !important;
  border:1px solid rgba(34,211,238,0.14) !important;
  border-radius:20px !important;
  box-shadow:0 32px 80px rgba(0,0,0,0.85) !important;
  color:#EFF6FF !important;
  font-family:'Plus Jakarta Sans',system-ui,sans-serif !important;
}
.as-root [role="dialog"] h2 { color:#EFF6FF !important; font-family:'Plus Jakarta Sans',system-ui,sans-serif !important; }
.as-root [role="dialog"] p  { color:#93B4D4 !important; }
/* close button */
.as-root [data-radix-dialog-close] svg { color:#93B4D4 !important; }
.as-root [data-radix-dialog-close]:hover svg { color:#22D3EE !important; }

/* ── animations ── */
@keyframes as-spin { to{transform:rotate(360deg)} }
@keyframes as-pulse { 0%,100%{opacity:.25}50%{opacity:.7} }
@keyframes as-glow { 0%,100%{opacity:0.4}50%{opacity:0.9} }

/* ── card base ── */
.as-card {
  background:linear-gradient(155deg,#0C1526 0%,#080F1D 100%);
  border:1px solid rgba(255,255,255,0.06);
  border-radius:18px;
  box-shadow:0 4px 24px rgba(0,0,0,0.35);
  position:relative; overflow:hidden;
  transition:all .25s cubic-bezier(0.4,0,0.2,1);
}
.as-card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:1px;
  background:linear-gradient(90deg,transparent,rgba(34,211,238,0.12),transparent);
}
.as-card:hover { box-shadow:0 8px 36px rgba(34,211,238,0.09); }

/* progress bar */
.as-prog-track { height:5px; background:rgba(255,255,255,0.05); border-radius:99px; overflow:hidden; }
.as-prog-fill  { height:100%; border-radius:99px; transition:width .7s cubic-bezier(0.4,0,0.2,1); }

/* tab btn */
.as-tab {
  display:inline-flex; align-items:center; gap:7px;
  padding:8px 18px; border-radius:10px; font-size:13px; font-weight:700;
  cursor:pointer; border:none; transition:all .2s; white-space:nowrap;
  font-family:'Plus Jakarta Sans',system-ui,sans-serif;
}

/* log entry */
.as-log { border-radius:12px; padding:13px 16px; transition:all .2s; }
.as-log:hover { filter:brightness(1.12); }

/* skeleton */
.as-skel { background:rgba(34,211,238,0.05); border-radius:8px; animation:as-pulse 1.8s ease-in-out infinite; }
`;

/* ─── Server definitions ─── */
const SERVERS_STATIC = [
  { id: "srv-001", name: "API Gateway", type: "gateway", region: "Singapore", ip: "103.45.67.89", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
  { id: "srv-002", name: "Payment Processor", type: "processor", region: "Singapore", ip: "", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
  { id: "srv-003", name: "Database Primary", type: "database", region: "Singapore", ip: "103.32.67.89", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
  { id: "srv-004", name: "Database Replica", type: "database", region: "Bangkok", ip: "", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
  { id: "srv-005", name: "Webhook Handler", type: "worker", region: "Singapore", ip: "", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
  { id: "srv-006", name: "Queue Processor", type: "worker", region: "Singapore", ip: "", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
  { id: "srv-007", name: "Load Balancer", type: "gateway", region: "Singapore", ip: "", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
  { id: "srv-008", name: "Redis Cache", type: "cache", region: "Singapore", ip: "", status: "healthy", isDataReal: false, uptime: 0, cpu: 0, memory: 0, disk: 0, network: { in: 0, out: 0 }, requests: 0, latency: 0 },
];

const TYPE_ICONS = { gateway: Globe, processor: Zap, database: Database, worker: Terminal, cache: MemoryStick };

const getTimeRangeStart = (range) => {
  const now = Date.now();
  switch (range) {
    case "1h": return new Date(now - 60 * 60 * 1000);
    case "6h": return new Date(now - 6 * 60 * 60 * 1000);
    case "24h": return new Date(now - 24 * 60 * 60 * 1000);
    case "7d": return new Date(now - 7 * 24 * 60 * 60 * 1000);
    default: return new Date(now - 24 * 60 * 60 * 1000);
  }
};

const bucketLogsToChartData = (apiLogs, dbLogs, range) => {
  const allLogs = [...(apiLogs || []), ...(dbLogs || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  if (!allLogs.length) return [];
  const bucketMs = range === "1h" ? 3 * 60000 : range === "6h" ? 15 * 60000 : range === "24h" ? 60 * 60000 : 6 * 60 * 60000;
  const start = getTimeRangeStart(range).getTime();
  const end = Date.now();
  const buckets = [];
  for (let t = start; t < end; t += bucketMs) {
    const bEnd = t + bucketMs;
    const inB = allLogs.filter(l => { const lt = new Date(l.createdAt).getTime(); return lt >= t && lt < bEnd; });
    const avgLat = inB.length ? Math.round(inB.reduce((s, l) => s + (l.latencyMs || 0), 0) / inB.length) : 0;
    const upCount = inB.filter(l => (l.latencyMs || 0) <= 50).length;
    const warnCount = inB.filter(l => (l.latencyMs || 0) > 50 && (l.latencyMs || 0) <= 200).length;
    const critCount = inB.filter(l => (l.latencyMs || 0) > 200).length;
    buckets.push({
      time: format(new Date(t + bucketMs / 2), range === "7d" ? "MM/dd" : "HH:mm"),
      latency: avgLat,
      requests: inB.length,
      healthy: upCount,
      warnings: warnCount,
      critical: critCount,
    });
  }
  return buckets;
};

const fetchSystemStatus = async () => {
  // Try with reqPerMin first, fall back to without if backend hasn't been updated yet
  const qFull = `query{systemStatus{apiGateway{status latencyMs uptimePercentage cpu memory disk reqPerMin networkIn networkOut}database{status latencyMs uptimePercentage cpu memory disk reqPerMin networkIn networkOut}}}`;
  const qReq = `query{systemStatus{apiGateway{status latencyMs uptimePercentage cpu memory disk reqPerMin}database{status latencyMs uptimePercentage cpu memory disk reqPerMin}}}`;
  const qBasic = `query{systemStatus{apiGateway{status latencyMs uptimePercentage cpu memory disk}database{status latencyMs uptimePercentage cpu memory disk}}}`;
  try {
    const res = await fetch(import.meta.env.VITE_API_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ query: qFull }) });
    const data = await res.json();
    if (data.data?.systemStatus) return data.data.systemStatus;
    // If network fields are unsupported, keep reqPerMin for live charts.
    const res2 = await fetch(import.meta.env.VITE_API_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ query: qReq }) });
    const data2 = await res2.json();
    if (data2.data?.systemStatus) return data2.data.systemStatus;
    const res3 = await fetch(import.meta.env.VITE_API_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ query: qBasic }) });
    const data3 = await res3.json();
    return data3.data?.systemStatus ?? null;
  } catch {
    return null;
  }
};

const mapBackendStatusToCardStatus = (status) => {
  if (status === "UP") return "healthy";
  if (status === "LOW") return "warning";
  if (status === "DOWN") return "critical";
  return "warning";
};

const formatBandwidth = (mbps) => {
  if (mbps == null || mbps <= 0) return "0 MB/s";
  if (mbps < 1) return `${(mbps * 1024).toFixed(1)} KB/s`;
  return `${mbps.toFixed(2)} MB/s`;
};

/* ─── Small reusable components ─── */
function StatCard({ icon: Icon, label, value, color, bgColor, delay = 0 }) {
  return (
    <motion.div
      className="as-card"
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      whileHover={{ y: -6, boxShadow: `0 14px 38px ${color}22` }}
      style={{ padding: "16px 20px", cursor: "default" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
        <div style={{ width: 42, height: 42, borderRadius: 13, background: bgColor, border: `1px solid ${color}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 0 14px ${color}15` }}>
          <Icon size={18} style={{ color }} />
        </div>
        <div>
          <p style={{ fontSize: 11, color: C.t3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", margin: 0 }}>{label}</p>
          <p style={{ fontSize: 20, fontWeight: 800, color, margin: "2px 0 0", letterSpacing: "-0.5px" }}>{value}</p>
        </div>
      </div>
    </motion.div>
  );
}

function ProgressBar({ label, value, color }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: C.t3, fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color }}>{value}%</span>
      </div>
      <div className="as-prog-track">
        <div className="as-prog-fill" style={{ width: `${value}%`, background: color, boxShadow: `0 0 6px ${color}60` }} />
      </div>
    </div>
  );
}

export default function AdminServers() {
  const [activeTab, setActiveTab] = useState("overview");
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState("24h");
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsServer, setLogsServer] = useState(null);
  const [serverLogs, setServerLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logStart, setLogStart] = useState("");
  const [logEnd, setLogEnd] = useState("");
  const [logStatus, setLogStatus] = useState("ALL");
  const [liveSeries, setLiveSeries] = useState([]);
  const { t } = useLanguage();

  const statusCfg = {
    healthy: { icon: CheckCircle2, color: C.emerald, bg: C.emeraldSoft, border: "rgba(16,185,129,0.3)", label: t("healthy") },
    warning: { icon: AlertTriangle, color: C.amber, bg: C.amberSoft, border: "rgba(245,158,11,0.3)", label: t("warning") },
    critical: { icon: XCircle, color: C.rose, bg: C.roseSoft, border: "rgba(251,113,133,0.3)", label: t("critical") },
    offline: { icon: XCircle, color: C.t3, bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.3)", label: t("offline") },
    disabled: { icon: XCircle, color: C.t4, bg: "rgba(36,58,82,0.3)", border: "rgba(36,58,82,0.5)", label: t("disabled") },
  };

  const srvName = (n) => ({ "API Gateway": t("apiGateway"), "Payment Processor": t("paymentProcessor"), "Database Primary": t("databasePrimary"), "Database Replica": t("databaseReplica"), "Webhook Handler": t("webhookHandler"), "Queue Processor": t("queueProcessor"), "Load Balancer": t("loadBalancer"), "Redis Cache": t("redisCache") }[n] || n);
  const srvRegion = (r) => ({ "Singapore": t("singapore"), "Bangkok": t("bangkok") }[r] || r);
  const logStatusLabel = (s) => ({ UP: t("statusUp"), LOW: t("statusLow"), CRITICAL: t("statusCritical"), DOWN: t("statusDown") }[s] || s);

  const { data: realStatus, refetch } = useQuery({
    queryKey: ["systemStatus"],
    queryFn: fetchSystemStatus,
  });

  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ["serverHistory", timeRange],
    queryFn: async () => {
      const start = getTimeRangeStart(timeRange).toISOString();
      const end = new Date().toISOString();
      const [apiData, dbData] = await Promise.all([
        getApiGatewayLogsFiltered(start, end, 1000),
        getDatabaseLogsFiltered(start, end, 1000),
      ]);
      return { api: apiData?.logs || [], db: dbData?.logs || [] };
    },
  });

  const historyChart = historyData ? bucketLogsToChartData(historyData.api, historyData.db, timeRange) : [];

  useEffect(() => {
    if (!realStatus) return;
    const api = realStatus.apiGateway || {};
    const db = realStatus.database || {};
    const sample = {
      time: format(new Date(), "HH:mm:ss"),
      cpu: Math.round(((api.cpu || 0) + (db.cpu || 0)) / 2),
      memory: Math.round(((api.memory || 0) + (db.memory || 0)) / 2),
      requests: (api.reqPerMin || 0) + (db.reqPerMin || 0),
      latency: Math.round(((api.latencyMs || 0) + (db.latencyMs || 0)) / 2),
    };
    setLiveSeries((prev) => [...prev.slice(-23), sample]);
  }, [realStatus]);

  const chartData = liveSeries.length
    ? liveSeries
    : [{ time: format(new Date(), "HH:mm:ss"), cpu: 0, memory: 0, requests: 0, latency: 0 }];

  /* Derived log values */
  const hasApiLogs = serverLogs?.logs?.length > 0 && logsServer?.id === "srv-001";
  const hasDbLogs = serverLogs?.logs?.length > 0 && (logsServer?.id === "srv-003" || logsServer?.id === "srv-004");
  const latestApiLog = hasApiLogs ? [...serverLogs.logs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : null;
  const latestDbLog = hasDbLogs ? [...serverLogs.logs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : null;
  const apiLatency = latestApiLog?.latencyMs ?? null;
  const dbLatency = latestDbLog?.latencyMs ?? null;

  /* Build server list — overlay real data onto static entries */
  const displayServers = SERVERS_STATIC.map(s => {
    if (s.id === "srv-001" && realStatus?.apiGateway) {
      const lat = apiLatency ?? realStatus.apiGateway.latencyMs ?? 0;
      const req = realStatus.apiGateway.reqPerMin || 0;
      const rawIn = realStatus.apiGateway.networkIn;
      const rawOut = realStatus.apiGateway.networkOut;
      const netIn = rawIn ?? null;
      const netOut = rawOut ?? null;
      return {
        ...s, isDataReal: true, status: mapBackendStatusToCardStatus(realStatus.apiGateway.status), latency: lat,
        uptime: realStatus.apiGateway.uptimePercentage || 100, cpu: realStatus.apiGateway.cpu || 0,
        memory: realStatus.apiGateway.memory || 0, disk: realStatus.apiGateway.disk || 0,
        network: { in: netIn, out: netOut }, requests: req
      };
    }
    if (s.id === "srv-003" && realStatus?.database) {
      const lat = dbLatency ?? realStatus.database.latencyMs ?? 0;
      const req = realStatus.database.reqPerMin || 0;
      const rawIn = realStatus.database.networkIn;
      const rawOut = realStatus.database.networkOut;
      const netIn = rawIn ?? null;
      const netOut = rawOut ?? null;
      return {
        ...s, isDataReal: true, status: mapBackendStatusToCardStatus(realStatus.database.status), latency: lat,
        uptime: realStatus.database.uptimePercentage || 100, cpu: realStatus.database.cpu || s.cpu,
        memory: realStatus.database.memory || s.memory, disk: realStatus.database.disk || s.disk,
        network: { in: netIn, out: netOut },
        requests: req
      };
    }
    return s;
  });

  const handleRefresh = () => { setRefreshing(true); Promise.all([refetch(), refetchHistory()]).then(() => setTimeout(() => setRefreshing(false), 600)); };

  const fetchLogs = async (srv, options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoadingLogs(true);
    try {
      const hasDate = logStart || logEnd;
      let data;
      if (srv.id === "srv-001" || srv.type === "gateway")
        data = hasDate ? await getApiGatewayLogsFiltered(logStart || null, logEnd || null, 100) : await getApiGatewayLogs(100);
      else if (srv.id === "srv-003" || srv.id === "srv-004" || srv.type === "database")
        data = hasDate ? await getDatabaseLogsFiltered(logStart || null, logEnd || null, 100) : await getDatabaseLogs(100);
      else
        data = { logs: [], serverStartTime: undefined, databaseConnectionTime: undefined, currentMemoryUsage: undefined };
      setServerLogs(data);
    } catch { setServerLogs({ error: t("failedToFetchLogs") }); }
    finally { if (!silent) setLoadingLogs(false); }
  };

  const handleViewLogs = async (srv, e) => {
    e.stopPropagation();
    setLogsServer(srv); setLogsOpen(true); setServerLogs(null);
    await fetchLogs(srv, { silent: false });
  };

  useEffect(() => {
    if (!logsServer || !logsOpen) return;
    fetchLogs(logsServer, { silent: false });
  }, [logStart, logEnd]);

  useEffect(() => {
    if (!logsServer || !logsOpen) return;

    const intervalId = setInterval(() => {
      fetchLogs(logsServer, { silent: true });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [logsServer, logsOpen, logStart, logEnd]);

  const realServers = displayServers.filter(s => s.isDataReal);
  const realCount = realServers.length || 1;
  const healthy = realServers.filter(s => s.status === "healthy").length;
  const warnings = realServers.filter(s => s.status === "warning").length;
  const critical = realServers.filter(s => s.status === "critical").length;
  const avgCpu = Math.round(realServers.reduce((a, s) => a + s.cpu, 0) / realCount);
  const avgMem = Math.round(realServers.reduce((a, s) => a + s.memory, 0) / realCount);

  // Use historical logs for latency & requests when available
  const allHistLogs = historyData ? [...(historyData.api || []), ...(historyData.db || [])] : [];
  const avgLat = allHistLogs.length
    ? Math.round(allHistLogs.reduce((a, l) => a + (l.latencyMs || 0), 0) / allHistLogs.length)
    : Math.round(realServers.reduce((a, s) => a + s.latency, 0) / realCount);
  const totalReq = allHistLogs.length || realServers.reduce((a, s) => a + s.requests, 0);

  const chartTip = { backgroundColor: C.cardHigh, border: `1px solid ${C.border}`, borderRadius: 12, color: C.t1, fontSize: 12 };

  const TAB_LIST = [
    { id: "overview", label: t("overview"), Icon: Server },
    { id: "performance", label: t("performance"), Icon: TrendingUp },
    { id: "network", label: t("network"), Icon: Wifi },
  ];

  /* ─── log entry display status ─── */
  const getLogStatus = (log) => {
    if ((logsServer?.id === "srv-001" || logsServer?.id === "srv-003" || logsServer?.type === "gateway" || logsServer?.type === "database") && log.latencyMs != null) {
      if (log.latencyMs > 200) return "CRITICAL";
      if (log.latencyMs > 50) return "LOW";
      return "UP";
    }
    return log.status;
  };

  const buildLogList = () => {
    if (!serverLogs?.logs?.length) return [];
    const sorted = [...serverLogs.logs];
    const hasDate = logStart || logEnd;

    if (hasDate) {
      let list = sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (logStatus !== "ALL") list = list.filter(l => getLogStatus(l) === logStatus);
      return list;
    }
    // Show status-change entries only (no date filter)
    const chronological = [...sorted].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const changes = []; let prev = null;
    for (const l of chronological) {
      const cur = getLogStatus(l);
      if (prev === null || cur !== prev) { changes.push(l); prev = cur; }
    }
    let result = changes.reverse();
    if (logStatus !== "ALL") result = result.filter(l => getLogStatus(l) === logStatus);
    return result;
  };

  return (
    <div className="as-root" style={{ minHeight: "100vh", background: C.bg }}>
      <style>{CSS}</style>

      {/* Background grid */}
      <div aria-hidden style={{
        position: "fixed", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(34,211,238,0.013) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.013) 1px,transparent 1px)",
        backgroundSize: "60px 60px",
        maskImage: "radial-gradient(ellipse 80% 80% at 50% 30%,black 20%,transparent 100%)"
      }} />
      <div aria-hidden style={{
        position: "fixed", inset: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse 1000px 600px at 5% 5%,rgba(34,211,238,0.04),transparent 70%),radial-gradient(ellipse 800px 500px at 95% 85%,rgba(167,139,250,0.04),transparent 65%)"
      }} />

      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6 relative z-10">

        {/* ─── HEADER ─── */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginBottom: 32 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg,rgba(34,211,238,0.14),rgba(59,130,246,0.14))",
              border: "1px solid rgba(34,211,238,0.28)", boxShadow: "0 8px 28px rgba(34,211,238,0.15)"
            }}>
              <Server size={24} style={{ color: C.cyan }} />
            </div>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: C.t1, margin: 0, letterSpacing: "-0.5px" }}>{t("serverInfrastructure")}</h1>
              <p style={{ fontSize: 13, color: C.t3, margin: "4px 0 0", fontWeight: 500 }}>{t("realTimeMonitoring")}</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger style={{ width: 134 }}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">{t("last1h")}</SelectItem>
                <SelectItem value="6h">{t("last6h")}</SelectItem>
                <SelectItem value="24h">{t("last24h")}</SelectItem>
                <SelectItem value="7d">{t("last7d")}</SelectItem>
              </SelectContent>
            </Select>
            <motion.button
              onClick={handleRefresh} disabled={refreshing}
              whileHover={{ scale: 1.04, y: -1 }} whileTap={{ scale: 0.96 }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8, padding: "0 18px", height: 40, borderRadius: 11,
                fontSize: 13, fontWeight: 700, cursor: refreshing ? "not-allowed" : "pointer",
                border: `1px solid ${C.border}`, background: C.card, color: C.t2,
                transition: "all .2s", opacity: refreshing ? 0.6 : 1, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif"
              }}
              onMouseEnter={e => { if (!refreshing) { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.cyan; e.currentTarget.style.boxShadow = "0 4px 14px rgba(34,211,238,0.14)"; } }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; e.currentTarget.style.boxShadow = "none"; }}
            >
              <RefreshCw size={14} style={{ animation: refreshing ? "as-spin 1s linear infinite" : "none" }} />
              {t("refresh")}
            </motion.button>
          </div>
        </motion.div>

        {/* ─── STAT CARDS ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(175px,1fr))", gap: 13, marginBottom: 28 }}>
          <StatCard icon={CheckCircle2} label={t("healthy")} value={`${healthy}/${realCount}`} color={C.emerald} bgColor={C.emeraldSoft} delay={0} />
          <StatCard icon={AlertTriangle} label={t("warnings")} value={warnings} color={C.amber} bgColor={C.amberSoft} delay={0.05} />
          <StatCard icon={XCircle} label={t("critical")} value={critical} color={C.rose} bgColor={C.roseSoft} delay={0.09} />
          <StatCard icon={Cpu} label={t("avgCpu")} value={`${avgCpu}%`} color={C.blue} bgColor={C.blueSoft} delay={0.12} />
          <StatCard icon={MemoryStick} label={t("avgMemory")} value={`${avgMem}%`} color={C.purple} bgColor={C.purpleSoft} delay={0.15} />
          <StatCard icon={Activity} label={t("requestsPerMin")} value={totalReq.toFixed(0)} color={C.cyan} bgColor={C.cyanSoft} delay={0.18} />
          <StatCard icon={Clock} label={t("avgLatency")} value={`${avgLat}ms`} color="#F472B6" bgColor="rgba(244,114,182,0.08)" delay={0.21} />
        </div>

        {/* ─── TABS ─── */}
        <div className="flex flex-wrap gap-[3px] rounded-[13px] p-1 mb-6 w-fit border"
          style={{
            background: C.card,
            borderColor: C.border,
          }}>
          {TAB_LIST.map(({ id, label, Icon }) => {
            const on = activeTab === id;
            return (
              <motion.button key={id} className="as-tab" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => setActiveTab(id)}
                style={{
                  background: on ? "rgba(34,211,238,0.1)" : "transparent", color: on ? C.cyan : C.t3,
                  boxShadow: on ? `0 0 0 1px rgba(34,211,238,0.24)` : "none"
                }}
              >
                <Icon size={13} />{label}
              </motion.button>
            );
          })}
        </div>

        {/* ─── OVERVIEW ─── */}
        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div
              key="ov"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full min-w-0"
            >
              {/* ✅ Responsive grid (no fixed min width) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 w-full min-w-0">
                {displayServers.map((srv, idx) => {
                  const displayStatus = !srv.isDataReal ? "disabled" : srv.status;
                  const sc = statusCfg[displayStatus];
                  const SI = sc.icon;
                  const TI = TYPE_ICONS[srv.type] || Server;
                  const dis = !srv.isDataReal;

                  const warnBorder =
                    !dis && srv.status === "warning"
                      ? "rgba(245,158,11,0.28)"
                      : !dis && srv.status === "critical"
                        ? "rgba(251,113,133,0.28)"
                        : undefined;

                  return (
                    <motion.div
                      key={srv.id}
                      className="as-card relative w-full min-w-0 p-5 sm:p-6"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={
                        !dis
                          ? {
                            y: -4,
                            boxShadow: "0 14px 44px rgba(34,211,238,0.12)",
                            borderColor: "rgba(34,211,238,0.32)",
                          }
                          : {}
                      }
                      style={{
                        opacity: dis ? 0.45 : 1,
                        borderColor: warnBorder || undefined,
                      }}
                    >
                      {/* top line accent */}
                      <div
                        className="absolute top-0 left-0 right-0 h-[2px]"
                        style={{
                          background: `linear-gradient(90deg,transparent,${sc.color}40,transparent)`,
                          opacity: dis ? 0 : 1,
                        }}
                      />

                      {/* header row */}
                      <div className="flex items-start justify-between gap-3 mb-4 min-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <motion.div
                            className="shrink-0 w-[46px] h-[46px] rounded-[14px] flex items-center justify-center"
                            style={{
                              background: sc.bg,
                              border: `1px solid ${sc.border}`,
                              boxShadow: `0 0 16px ${sc.color}20`,
                            }}
                            whileHover={{ scale: 1.1, rotate: -5 }}
                            transition={{ type: "spring", stiffness: 400, damping: 20 }}
                          >
                            <TI size={20} style={{ color: sc.color }} />
                          </motion.div>

                          <div className="min-w-0">
                            <h3
                              className="text-[14px] font-extrabold tracking-[-0.2px] truncate"
                              style={{ color: C.t1 }}
                              title={srvName(srv.name)}
                            >
                              {srvName(srv.name)}
                            </h3>
                            <p className="text-[11px] font-medium mt-1 min-w-0 truncate" style={{ color: C.t3 }}>
                              {srvRegion(srv.region)} {srv.ip ? `• ${srv.ip}` : ""}
                            </p>
                          </div>
                        </div>

                        <span
                          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-[9px] text-[11px] font-extrabold whitespace-nowrap"
                          style={{ background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color }}
                        >
                          <SI size={10} />
                          {sc.label}
                        </span>
                      </div>

                      {/* progress bars */}
                      <div className="flex flex-col gap-3 mb-4">
                        <ProgressBar label={t("cpu")} value={srv.cpu} color={srv.cpu > 80 ? C.rose : srv.cpu > 60 ? C.amber : C.emerald} />
                        <ProgressBar label={t("memory")} value={srv.memory} color={srv.memory > 85 ? C.rose : srv.memory > 70 ? C.amber : C.blue} />
                        <ProgressBar label={t("disk")} value={srv.disk} color={C.purple} />
                      </div>

                      {/* stats row */}
                      <div className="grid grid-cols-3 gap-2 py-3 border-t mb-3" style={{ borderColor: C.border }}>
                        {[
                          { label: t("latency"), value: `${srv.latency}ms`, color: C.t1 },
                          { label: t("uptime"), value: `${srv.uptime}%`, color: C.emerald },
                          { label: t("req"), value: Math.round(srv.requests) || 0, color: C.t1 },
                        ].map(({ label, value, color }) => (
                          <div key={label} className="text-center min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-[0.06em] mb-1 truncate" style={{ color: C.t3 }}>
                              {label}
                            </p>
                            <p className="text-[15px] font-extrabold m-0 truncate" style={{ color }}>
                              {value}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* view logs btn */}
                      <motion.button
                        onClick={(e) => handleViewLogs(srv, e)}
                        disabled={!srv.isDataReal}
                        whileHover={srv.isDataReal ? { scale: 1.02 } : {}}
                        whileTap={srv.isDataReal ? { scale: 0.97 } : {}}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[12px] font-extrabold border transition min-w-0"
                        style={{
                          borderColor: C.border,
                          background: "transparent",
                          color: C.t2,
                          opacity: !srv.isDataReal ? 0.35 : 1,
                          cursor: !srv.isDataReal ? "not-allowed" : "pointer",
                          fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif",
                        }}
                        onMouseEnter={(e) => {
                          if (!srv.isDataReal) return;
                          e.currentTarget.style.borderColor = C.borderHov;
                          e.currentTarget.style.color = C.cyan;
                          e.currentTarget.style.background = C.cyanSoft;
                          e.currentTarget.style.boxShadow = "0 0 14px rgba(34,211,238,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = C.border;
                          e.currentTarget.style.color = C.t2;
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <FileText size={13} />
                        <span className="truncate">{t("viewLogs")}</span>
                        {srv.isDataReal && <ChevronRight size={12} className="shrink-0" />}
                      </motion.button>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ─── PERFORMANCE ─── */}
          {activeTab === "performance" && (
            <motion.div key="perf" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 w-full min-w-0">
                {[
                  { base: t("cpuUsage"), key: "cpu", color: C.blue, Icon: Cpu, gid: "g1", type: "area", useLive: true },
                  { base: t("memoryUsage"), key: "memory", color: C.purple, Icon: MemoryStick, gid: "g2", type: "area", useLive: true },
                  { base: t("requestsPerMin") || "Requests", key: "requests", color: C.emerald, Icon: Activity, gid: "g3", type: "line" },
                  { base: t("responseLatency"), key: "latency", color: "#F472B6", Icon: Clock, gid: "g4", type: "line", unit: "ms" },
                ].map(({ base, key, color, Icon, gid, type, unit = "", useLive }) => {
                  const data = useLive ? chartData : (historyChart.length ? historyChart : chartData);
                  const rangeLabel = useLive ? "Live" : ({ "1h": "1h", "6h": "6h", "24h": "24h", "7d": "7d" }[timeRange] || timeRange);
                  const title = `${base} (${rangeLabel})`;
                  return (
                    <motion.div key={key} className="as-card"
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      style={{ padding: 24 }}
                      whileHover={{ y: -3, boxShadow: `0 12px 36px ${color}18` }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 11, background: `${color}14`, border: `1px solid ${color}28`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Icon size={16} style={{ color }} />
                        </div>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: C.t1, margin: 0 }}>{title}</h3>
                      </div>
                      <div style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {type === "area" ? (
                            <AreaChart data={data}>
                              <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.28} />
                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                              </linearGradient></defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                              <XAxis dataKey="time" stroke={C.t4} fontSize={10} />
                              <YAxis stroke={C.t4} fontSize={10} domain={[0, 100]} />
                              <Tooltip contentStyle={chartTip} />
                              <Area type="monotone" dataKey={key} stroke={color} strokeWidth={2} fill={`url(#${gid})`} />
                            </AreaChart>
                          ) : (
                            <LineChart data={data}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                              <XAxis dataKey="time" stroke={C.t4} fontSize={10} />
                              <YAxis stroke={C.t4} fontSize={10} unit={unit} />
                              <Tooltip contentStyle={chartTip} formatter={v => [`${v}${unit}`, title]} />
                              <Line type="monotone" dataKey={key} stroke={color} strokeWidth={2} dot={false} />
                            </LineChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ─── NETWORK ─── */}
          {activeTab === "network" && (
            <motion.div key="net" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <div className="as-card" style={{ overflow: "hidden" }}>
                <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                  <Wifi size={16} style={{ color: C.cyan }} />
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: C.t1, margin: 0 }}>{t("networkTraffic")}</h3>
                </div>
                {displayServers.map((srv, idx) => {
                  const enabled = srv.id === "srv-001" || srv.id === "srv-003";
                  return (
                    <motion.div
                      key={srv.id}
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }}
                      style={{
                        display: "flex", alignItems: "center", gap: 16, padding: "14px 24px",
                        opacity: enabled ? 1 : 0.38, cursor: enabled ? "default" : "not-allowed",
                        borderBottom: `1px solid ${C.border}`, transition: "background .2s"
                      }}
                      onMouseEnter={e => { if (enabled) e.currentTarget.style.background = "rgba(34,211,238,0.03)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: 11,
                        background: enabled ? C.cyanSoft : "rgba(255,255,255,0.03)",
                        border: `1px solid ${enabled ? "rgba(34,211,238,0.2)" : C.border}`,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                      }}>
                        <Server size={16} style={{ color: enabled ? C.cyan : C.t4 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: enabled ? C.t1 : C.t3, margin: 0 }}>{srvName(srv.name)}</p>
                        <p style={{ fontSize: 11, color: C.t3, margin: "3px 0 0" }}>{srv.ip || t("na")}</p>
                      </div>
                      <div style={{ display: "flex", gap: 28 }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, color: enabled ? C.emerald : C.t4, justifyContent: "flex-end" }}>
                            <ArrowDown size={12} />
                            <span style={{ fontSize: 13, fontWeight: 800 }}>{enabled ? (srv.network.in == null ? t("na") : formatBandwidth(srv.network.in)) : t("na")}</span>
                          </div>
                          <p style={{ fontSize: 10, color: C.t3, margin: "2px 0 0", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("inbound")}</p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, color: enabled ? C.blue : C.t4, justifyContent: "flex-end" }}>
                            <ArrowUp size={12} />
                            <span style={{ fontSize: 13, fontWeight: 800 }}>{enabled ? (srv.network.out == null ? t("na") : formatBandwidth(srv.network.out)) : t("na")}</span>
                          </div>
                          <p style={{ fontSize: 10, color: C.t3, margin: "2px 0 0", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("outbound")}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─────────────── LOGS DIALOG ─────────────── */}
        <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
          <DialogContent style={{
            maxWidth: 820, maxHeight: "88vh", overflowY: "auto",
            background: C.card, border: `1px solid rgba(34,211,238,0.16)`,
            borderRadius: 20, boxShadow: "0 32px 80px rgba(0,0,0,0.85)", color: C.t1
          }}>

            {/* Dialog top border accent */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2, borderRadius: "20px 20px 0 0",
              background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.6) 50%,transparent)",
              pointerEvents: "none"
            }} />

            <DialogHeader style={{ marginBottom: 4 }}>
              <DialogTitle style={{ color: C.t1, fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 10, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif" }}>
                <div style={{ width: 36, height: 36, borderRadius: 11, background: C.cyanSoft, border: `1px solid rgba(34,211,238,0.25)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FileText size={16} style={{ color: C.cyan }} />
                </div>
                {t("serverLogsTitle")?.replace("{name}", logsServer?.name || "")}
              </DialogTitle>
              <DialogDescription style={{ color: C.t3, fontSize: 13 }}>{t("viewServerLogsDescription")}</DialogDescription>
            </DialogHeader>

            {loadingLogs ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "52px 0", gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 50, background: C.cyanSoft, border: `1px solid rgba(34,211,238,0.2)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <RefreshCw size={20} style={{ color: C.cyan, animation: "as-spin 1s linear infinite" }} />
                </div>
                <p style={{ color: C.t2, fontSize: 14, margin: 0, fontWeight: 600 }}>{t("loadingLogs")}</p>
              </div>
            ) : serverLogs?.error ? (
              <div style={{ textAlign: "center", padding: "48px 0", color: C.rose }}>
                <AlertTriangle size={28} style={{ margin: "0 auto 12px" }} />
                <p style={{ fontWeight: 700 }}>{serverLogs.error}</p>
              </div>
            ) : serverLogs ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>

                {/* ── Info grid ── */}
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12,
                  background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}`, borderRadius: 14, padding: 18
                }}>
                  {[
                    { label: t("serverStarted"), value: serverLogs.serverStartTime ? format(new Date(serverLogs.serverStartTime), "PPpp") : t("na") },
                    { label: t("databaseConnectionEstablished"), value: serverLogs.databaseConnectionTime ? format(new Date(serverLogs.databaseConnectionTime), "PPpp") : t("na") },
                    { label: t("currentMemoryUsage"), value: serverLogs.currentMemoryUsage != null ? `${serverLogs.currentMemoryUsage}%` : t("na") },
                    { label: t("totalLogEntries"), value: serverLogs.totalLogEntries ?? (serverLogs.logs?.length || 0) },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p style={{ fontSize: 10, color: C.t3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.09em", margin: "0 0 5px" }}>{label}</p>
                      <p style={{ fontSize: 13, fontWeight: 700, color: C.t1, margin: 0 }}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* ── Filters ── */}
                <div style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
                  <p style={{ fontSize: 11, color: C.t3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.09em", margin: "0 0 12px" }}>
                    {t("filterByStatus")}
                  </p>
                  <Select value={logStatus} onValueChange={setLogStatus}>
                    <SelectTrigger style={{ width: "100%" }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">{t("allStatuses")}</SelectItem>
                      <SelectItem value="UP">{t("statusUp")}</SelectItem>
                      <SelectItem value="LOW">{t("statusLow")}</SelectItem>
                      <SelectItem value="CRITICAL">{t("statusCritical")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* ── Date filter — FULLY DARK ── */}
                <div style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
                  <p style={{ fontSize: 11, color: C.t3, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.09em", margin: "0 0 12px" }}>
                    {t("dateRange") || "Date Range"}
                  </p>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={{ fontSize: 11, color: C.t3, fontWeight: 700, display: "block", marginBottom: 6 }}>{t("fromDate")}</label>
                      <input
                        type="date"
                        className="as-date"
                        value={logStart}
                        onChange={e => setLogStart(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={{ fontSize: 11, color: C.t3, fontWeight: 700, display: "block", marginBottom: 6 }}>{t("toDate")}</label>
                      <input
                        type="date"
                        className="as-date"
                        value={logEnd}
                        onChange={e => setLogEnd(e.target.value)}
                      />
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                      onClick={() => { setLogStart(""); setLogEnd(""); setLogStatus("ALL"); }}
                      style={{
                        height: 40, padding: "0 18px", borderRadius: 11, fontSize: 12, fontWeight: 800, cursor: "pointer",
                        border: `1px solid ${C.border}`, background: "transparent", color: C.t2, transition: "all .2s",
                        fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", whiteSpace: "nowrap"
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = C.cyan; e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.background = C.cyanSoft; }}
                      onMouseLeave={e => { e.currentTarget.style.color = C.t2; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = "transparent"; }}
                    >
                      {t("reset")}
                    </motion.button>
                  </div>
                </div>

                {/* ── Log entries ── */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <h4 style={{ fontSize: 13, fontWeight: 800, color: C.t2, margin: 0 }}>{t("recentLogEntries")}</h4>
                    {serverLogs.logs?.length > 0 && (
                      <span style={{ fontSize: 11, color: C.t3, fontWeight: 600 }}>
                        {buildLogList().length} {t("entries") || "entries"}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
                    <AnimatePresence>
                      {buildLogList().length > 0
                        ? buildLogList().map((log, i) => {
                          const ds = getLogStatus(log);
                          const isUp = ds === "UP", isLow = ds === "LOW", isCrit = ds === "CRITICAL", isDn = ds === "DOWN";
                          const lc = isUp ? C.emerald : isLow ? C.amber : C.rose;
                          const LI = isUp ? CheckCircle2 : isLow ? AlertTriangle : XCircle;
                          return (
                            <motion.div key={log.id || i} className="as-log"
                              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                              style={{ border: `1px solid ${lc}22`, background: `${lc}08` }}
                            >
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                                <div style={{ width: 28, height: 28, borderRadius: 8, background: `${lc}14`, border: `1px solid ${lc}28`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <LI size={13} style={{ color: lc }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 3 }}>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: lc }}>{logStatusLabel(ds)}</span>
                                    {log.latencyMs != null && (
                                      <span style={{ fontSize: 11, color: C.t3, fontFamily: "'JetBrains Mono',monospace" }}>
                                        {log.latencyMs}ms
                                      </span>
                                    )}
                                  </div>
                                  <p style={{ fontSize: 11, color: C.t3, margin: "2px 0 0", fontFamily: "'JetBrains Mono',monospace" }}>
                                    {format(new Date(log.createdAt), "PPpp")}
                                  </p>
                                  {isDn && <p style={{ fontSize: 11, color: C.rose, margin: "4px 0 0", fontWeight: 600 }}>{t("connectionFailed")}</p>}
                                  {isCrit && <p style={{ fontSize: 11, color: C.rose, margin: "4px 0 0", fontWeight: 600 }}>{t("criticalLatencyDetected")}</p>}
                                  {isLow && <p style={{ fontSize: 11, color: C.amber, margin: "4px 0 0", fontWeight: 600 }}>{t("highLatencyDetected")}</p>}
                                </div>
                              </div>
                            </motion.div>
                          );
                        })
                        : (
                          <div style={{ textAlign: "center", padding: "36px 0", color: C.t3 }}>
                            <FileText size={28} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
                            <p style={{ fontSize: 13, fontWeight: 600 }}>{t("noLogsAvailable")}</p>
                          </div>
                        )
                      }
                    </AnimatePresence>
                  </div>
                </div>

              </div>
            ) : null}
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
}